import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import i18next from '../../../i18n';
import styles from './ProductListingPage.module.scss';
import v2 from './ProductListingPageV2.module.scss';
import AppPaths from '../../AppPaths';
import { getDomainConfig } from '../../lib/domainConfig';
import { series3PlusCollarId } from '../../types/Product';
import { inAppShop, partnerLandingPage, productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { getPartnerSlugForCode } from '../../views/PartnerLanding/partnersRegistry';
import { usePartnerLandingEnabled } from '../../views/PartnerLanding/usePartnerLandingEnabled';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import FiMiniForCatsImage from '../../assets/images/plp-fi-mini-cats.png';
import FiAccessoriesImage from '../../assets/images/plp-accessories.webp';
import FiSupplementsImage from '../../assets/images/plp-supplements.webp';
import FiNanoMicrochipsImage from '../../assets/images/plp-nano.webp';
import { ReactComponent as DownArrow } from '../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../assets/images/icons/arrow_up.svg';
import { ReactComponent as ArrowRight } from '../../assets/images/icons/arrow_right.svg';
import { ReactComponent as Check } from '../../assets/images/check.svg';
import { ReactComponent as IconMagic } from '../../assets/images/icons/icon_magic.svg';
import { ReactComponent as IconFeather } from '../../assets/images/icons/icon_feather.svg';
import { ReactComponent as IconReinforced } from '../../assets/images/icons/icon_reinforced.svg';
import { ReactComponent as IconSatellite } from '../../assets/images/icons/icon_satellite.svg';
import { ReasonIcon } from '../CompareV2/CompareIcons';
import { ReactComponent as Dog } from '../../assets/images/icons/dog.svg';
import { ReactComponent as Cat } from '../../assets/images/icons/cat.svg';
import classNames from 'classnames';
import {
  collarProductIsSupportedInCountry,
  ultraIsDiscoverable,
  nanoProductIsSupportedInCountry,
  supplementsProductIsSupportedInCountry,
  useLocale,
} from '../../contexts/LocaleContext';
import * as types from '../../types';
import { Button, Flex, Text } from '@mantine/core';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { QuizAutoOpen } from '../ProductQuiz/QuizAutoOpen';
import FreeTrialCard from '../FreeTrialCard/FreeTrialCard';
import { getUrlPartnerCode } from '../../lib/giftWithPurchasePromo';
import { isInAppShop } from '../../lib/inAppShop';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';
import useHardwareFirstShop from '../../hooks/useHardwareFirstShop';
import { usePlpSupplementsCardEnabled } from '../../hooks/useSupplementsFlags';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import StarRating from '../StarRating/StarRating';

function useRedeemFlow() {
  const discountParam = getUrlPartnerCode();
  const isRedeemFlow = !!discountParam?.trim();

  return { discountParam, isRedeemFlow };
}

type PlpCardLabel = string;

function getPlpCardLabel(title: string, subtitle?: string): PlpCardLabel {
  const series3PlusTitle = i18next.t('series_3_plus');
  const miniTitle = i18next.t('mini');
  const ultraTitle = i18next.t('fi_ultra');
  const forCatsSubtitle = i18next.t('for_cats');

  if (title === series3PlusTitle) return i18next.t('series_3_plus');
  if (title === ultraTitle) return i18next.t('fi_ultra');
  if (title === miniTitle && subtitle && subtitle.toUpperCase().includes(forCatsSubtitle.toUpperCase()))
    return i18next.t('mini_cats');
  return i18next.t('mini');
}

function trackDetailsToggle(title: string, subtitle: string | undefined, expanded: boolean) {
  const label = getPlpCardLabel(title, subtitle);
  const miniLabel = i18next.t('mini');
  const series3PlusLabel = i18next.t('series_3_plus');
  const miniCatsLabel = i18next.t('mini_cats');
  const ultraLabel = i18next.t('fi_ultra');

  if (label === miniLabel) {
    expanded ? productListingPage.miniDetailsExpand() : productListingPage.miniDetailsCollapse();
  } else if (label === series3PlusLabel) {
    expanded ? productListingPage.series3PlusDetailsExpand() : productListingPage.series3PlusDetailsCollapse();
  } else if (label === miniCatsLabel) {
    expanded ? productListingPage.miniCatsDetailsExpand() : productListingPage.miniCatsDetailsCollapse();
  } else if (label === ultraLabel) {
    expanded ? productListingPage.ultraDetailsExpand() : productListingPage.ultraDetailsCollapse();
  }
}

type BetterForProps = { children: React.ReactNode; color: string; icon: string };
const ProductCardBetterFor: React.FC<BetterForProps> = ({ children, color, icon }) => (
  <span className={classNames(styles.betterForItem, styles[color])}>
    {icon === 'dog' ? <Dog /> : <Cat />}
    {children}
  </span>
);

type CardProps = {
  title: string;
  subtitle?: string;
  description: string;
  image: string;
  cta: string;
  onClick: () => void;
  hardware?: (string | { text: string; icon: React.ReactNode })[];
  betterFor?: { color: string; label: string; icon: string }[];
  showSaleBadge?: boolean;
  freeTrialAvailable?: boolean;
  /** `v2` is the pricing-prototype card: bullets always open, full-width cta, no details drawer. */
  variant?: 'classic' | 'v2';
};

const ProductCard: React.FC<CardProps> = ({
  title,
  subtitle,
  description,
  image,
  cta,
  onClick,
  hardware = [],
  betterFor = [],
  showSaleBadge = false,
  freeTrialAvailable = false,
  variant = 'classic',
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const hasDetails = hardware.length > 0 || betterFor.length > 0;

  if (variant === 'v2') {
    return (
      <div className={v2.card}>
        <img className={v2.cardImage} src={image} alt={title} loading="lazy" />
        <div className={v2.cardBody}>
          <div className={v2.cardTitle}>{title}</div>
          <div className={v2.cardDescription}>{description}</div>
          <ul className={v2.cardBullets}>
            {hardware.map((h) => {
              const isCustom = typeof h !== 'string';
              return (
                <li key={isCustom ? h.text : h}>
                  {isCustom ? h.icon : <Check />}
                  {isCustom ? h.text : h}
                </li>
              );
            })}
          </ul>
          <button type="button" className={v2.cardCta} onClick={onClick}>
            {cta}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.productCard}>
      <img className={styles.cardImage} src={image} alt={title} loading="lazy" />
      <div className={styles.cardBody}>
        <div className={styles.cardTitle}>
          {title}
          <div className={styles.cardTitleSubtitle}>{subtitle}</div>
          {showSaleBadge && <SaleBadge className={styles.saleBadge} />}
        </div>
        <div className={styles.cardDescription}>{description}</div>
        <div className={styles.cardButtonRow}>
          <button className={styles.cardButton} onClick={onClick}>
            {cta}
          </button>
          {freeTrialAvailable && <div className={styles.freeTrialAvailableText}>{t('free_trial')}</div>}
        </div>
        {hasDetails && (
          <>
            <div className={styles.divider} />
            <button
              type="button"
              className={styles.detailsToggle}
              aria-expanded={showDetails}
              onClick={() =>
                setShowDetails((s) => {
                  const next = !s;
                  trackDetailsToggle(title, subtitle, next);
                  return next;
                })
              }
            >
              <div className={styles.closeContainer}>
                <span className={styles.closeContainerTextMobile}>{t('hide_details')}</span>
                <span className={styles.closeContainerTextDesktop}>{t('details')}</span>
                <UpArrow />
              </div>
              <div className={styles.openContainer}>
                <span className={styles.openContainerTextMobile}>{t('see_details')}</span>
                <span className={styles.openContainerTextDesktop}>{t('details')}</span>
                <DownArrow />
              </div>
            </button>

            <div className={classNames(styles.detailsSection, { [styles.open]: showDetails })}>
              <div className={styles.hardwareTitle}>{t('hardware_no_colon')}</div>
              <div className={styles.hardware}>
                {hardware.map((h) => {
                  const isCustom = typeof h !== 'string';
                  return (
                    <div className={styles.hardwareItem} key={isCustom ? h.text : h}>
                      {isCustom ? h.icon : <Check />}
                      {isCustom ? h.text : h}
                    </div>
                  );
                })}
              </div>
              <div className={styles.betterForTitle}>{t('better_for')}</div>
              <div className={styles.betterFor}>
                {betterFor.map((a) => (
                  <ProductCardBetterFor key={a.label} color={a.color} icon={a.icon}>
                    {a.label}
                  </ProductCardBetterFor>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProductCarousel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={classNames(styles.carousel, className)}>{children}</div>
);

type OrderableProductCard = { key: string; visible: boolean; node: React.ReactNode };

/**
 * Orders the main product cards by the `o` query param (e.g. `?o=u-s3-m-mc`).
 * Tokens are hyphen-separated card keys; unknown or duplicate tokens are
 * ignored, and any visible card the param omits is appended in its default
 * position so a partial `o` never silently hides a product. Hidden cards
 * (country/flow gated) are never surfaced even when explicitly requested.
 * Falls back to the default order when the param is absent.
 */
function orderProductCards(cards: OrderableProductCard[], orderParam: string | null): OrderableProductCard[] {
  const visible = cards.filter((c) => c.visible);
  if (!orderParam) return visible;

  const byKey = new Map(visible.map((c) => [c.key, c]));
  const seen = new Set<string>();
  const ordered = orderParam
    .split('-')
    .map((token) => token.trim().toLowerCase())
    .flatMap((key) => {
      const card = byKey.get(key);
      if (!card || seen.has(key)) return [];
      seen.add(key);
      return [card];
    });

  return [...ordered, ...visible.filter((c) => !seen.has(c.key))];
}

const ProductListingPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();
  const { search } = useLocation();
  const scrollFiredRef = useRef<{ [k: string]: boolean }>({ '50': false, '75': false, '100': false });
  const { country } = useLocale();
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  const { discountParam, isRedeemFlow } = useRedeemFlow();
  // If the `ecom-partner-landing-pages` GrowthBook flag is on (prod) or we're
  // in a non-prod build, and the partner code's prefix maps to a registered
  // partner landing page, redirect there while preserving the full query
  // string (so `?pc=...` is kept).
  const partnerLandingEnabled = usePartnerLandingEnabled();
  const partnerSlugRedirect = partnerLandingEnabled ? getPartnerSlugForCode(discountParam) : null;
  const isUltraSupportedInCountry = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const isFreeTrialVisible = useFreeTrialPromoVisible();
  const inApp = isInAppShop();
  const isReferralV2 = useIsReferralCheckoutV2();
  const referrerName = useSelector((state: types.AppState) => state.storeShop.cart.referrerName);
  // in the hardware-first arm the tracker cards open /shop narrowed to that one device, rather
  // than that device's own pdp: the shopper already picked here, so the shop shows no rail to
  // re-pick from. marketing keeps this page on the classic listing either way.
  const shopEnabled = useHardwareFirstShop();
  const supplementsCardEnabled = usePlpSupplementsCardEnabled();
  // the pricing-prototype redesign is the plain listing. the redeem, referral and in-app variants
  // carry their own copy and keep the classic layout.
  const plpV2 = !isRedeemFlow && !isReferralV2 && !inApp;

  useEffect(() => {
    if (partnerSlugRedirect) {
      partnerLandingPage.redirectedFromPlp(partnerSlugRedirect);
      // Skip PLP analytics + scroll tracking for redirected sessions so the
      // partner landing gets the page-view signal instead of the PLP.
      return;
    }
    productListingPage.pageViewed();
    if (inApp) {
      inAppShop.opened();
    }

    const onScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const height = (document.documentElement.scrollHeight || 0) - window.innerHeight;
      if (height <= 0) {
        return;
      }
      const pct = (scrollTop / height) * 100;
      if (!scrollFiredRef.current['50'] && pct >= 50) {
        productListingPage.scroll50();
        scrollFiredRef.current['50'] = true;
      }
      if (!scrollFiredRef.current['75'] && pct >= 75) {
        productListingPage.scroll75();
        scrollFiredRef.current['75'] = true;
      }
      if (!scrollFiredRef.current['100'] && pct >= 100) {
        productListingPage.scroll100();
        scrollFiredRef.current['100'] = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, [partnerSlugRedirect, inApp]);

  // Country gating runs BEFORE the partner redirect so that non-Mini countries
  // continue to land on the Series 3+ PDP as they did before this feature.
  // Partner pages surface a Mini CTA unconditionally, so redirecting Mini-
  // unsupported countries into them would leak a broken call-to-action.
  // (Today the only Mini-supported country is US — see LocaleContext.)
  if (!collarProductIsSupportedInCountry(types.miniCollarId, country)) {
    return <Redirect to={AppPaths.Products(types.series3PlusCollarId)} />;
  }

  if (partnerSlugRedirect) {
    return <Redirect to={`${AppPaths.Partners.LandingFor(partnerSlugRedirect)}${search}`} />;
  }

  const miniBatteryLife = t('up_to_2_months_battery_life');
  const variant = plpV2 ? 'v2' : 'classic';
  // v2 bullets always show, so every row carries an icon
  const attachable = { text: t('attachable_to_collars_and_harnesses'), icon: <ReasonIcon name="link" /> };
  const lightweight = { text: t('small_and_lightweight'), icon: <IconFeather /> };
  const miniBattery = { text: miniBatteryLife, icon: <ReasonIcon name="battery" /> };
  const intelligence = { text: t('fi_intelligence_enabled'), icon: <IconMagic /> };

  // Card display order can be overridden via `?o=` (e.g. `?o=u-s3-m-mc`). v2 leads with mini.
  const orderParam = new URLSearchParams(search).get('o') ?? (plpV2 ? 'm-s3-u-mc' : null);
  const productCards: OrderableProductCard[] = [
    {
      key: 's3',
      visible: true,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_series_3_plus') : 'Series 3+'}
          description={plpV2 ? t('plp_v2_desc_series_3_plus') : t('product_description_series_3_plus')}
          image={FiSeries3PlusImage}
          cta={isRedeemFlow ? t('claim_free_series_3_plus') : t('shop_fi_series_3_plus')}
          onClick={() => {
            productListingPage.series3PlusShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.Redeem}?discount=${encodeURIComponent(discountParam ?? '')}`
                : shopEnabled
                  ? AppPaths.ShopForDeviceOnly('s3')
                  : AppPaths.Products(series3PlusCollarId),
            );
          }}
          hardware={
            plpV2
              ? [
                  { text: t('built_directly_into_the_collar'), icon: <IconReinforced /> },
                  { text: t('high_durability'), icon: <ReasonIcon name="shield" /> },
                  { text: t('up_to_3_months_of_battery_life'), icon: <ReasonIcon name="battery" /> },
                  intelligence,
                ]
              : [
                  t('high_durability'),
                  t('built_directly_into_the_collar'),
                  t('up_to_3_months_of_battery_life'),
                  intelligence,
                ]
          }
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'm',
      visible: true,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_mini') : 'Mini'}
          description={plpV2 ? t('plp_v2_desc_mini') : t('product_description_mini')}
          image={FiMiniImage}
          cta={isRedeemFlow ? t('claim_free_mini') : t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniShopNow();
            history.push(
              isRedeemFlow
                ? `${AppPaths.MiniRedeem}?discount=${encodeURIComponent(discountParam ?? '')}&type=dog`
                : shopEnabled
                  ? AppPaths.ShopForDeviceOnly('mini')
                  : AppPaths.Products('fi-mini') + '?type=dog',
            );
          }}
          hardware={
            plpV2
              ? [attachable, lightweight, miniBattery, intelligence]
              : [t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife, intelligence]
          }
          betterFor={[
            { color: 'yellow', label: t('small_dogs'), icon: 'dog' },
            { color: 'yellow', label: t('puppies'), icon: 'dog' },
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
    {
      key: 'u',
      visible: isUltraSupportedInCountry && !isRedeemFlow,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_ultra') : 'Ultra'}
          description={t('product_description_ultra')}
          image={FiUltraImage}
          cta={t('shop_fi_ultra')}
          onClick={() => {
            productListingPage.ultraShopNow();
            history.push(shopEnabled ? AppPaths.ShopForDeviceOnly('ultra') : AppPaths.Products('fi-ultra'));
          }}
          hardware={
            plpV2
              ? [
                  attachable,
                  { text: t('satellite_tracking_with_starlink'), icon: <IconSatellite /> },
                  { text: t('plp_v2_up_to_5_days_of_battery'), icon: <ReasonIcon name="battery" /> },
                  { text: t('sound_and_vibration_feedback'), icon: <ReasonIcon name="callback" /> },
                ]
              : [
                  t('attachable_to_collars_and_harnesses'),
                  t('satellite_tracking_with_starlink'),
                  t('sound_and_vibration_feedback'),
                ]
          }
          betterFor={[
            { color: 'blue', label: t('medium_dogs'), icon: 'dog' },
            { color: 'green', label: t('large_dogs'), icon: 'dog' },
            { color: 'green', label: t('extra_large_dogs'), icon: 'dog' },
          ]}
          showSaleBadge={true}
          freeTrialAvailable={false}
        />
      ),
    },
    {
      key: 'mc',
      visible: !isRedeemFlow && !isReferralV2,
      node: (
        <ProductCard
          variant={variant}
          title={plpV2 ? t('plp_v2_card_mini_cats') : 'Mini'}
          subtitle={plpV2 ? undefined : t('for_cats')}
          description={t('product_description_mini_cats')}
          image={FiMiniForCatsImage}
          cta={plpV2 ? t('plp_v2_shop_fi_mini_cats') : t('shop_fi_mini')}
          onClick={() => {
            productListingPage.miniCatsShopNow();
            history.push(shopEnabled ? AppPaths.ShopForCats : AppPaths.Products('fi-mini') + '?type=cat');
          }}
          hardware={
            plpV2
              ? [attachable, lightweight, miniBattery, intelligence]
              : [t('attachable_to_collars_and_harnesses'), t('small_and_lightweight'), miniBatteryLife]
          }
          betterFor={[{ color: 'yellow', label: t('cats'), icon: 'cat' }]}
          showSaleBadge={true}
          freeTrialAvailable={!isRedeemFlow && !inApp && isFreeTrialVisible}
        />
      ),
    },
  ];
  const orderedProductCards = orderProductCards(productCards, orderParam);

  const keepShopping = [
    {
      title: t('accessories'),
      description: t('shop_bases_collars_attachments_and_more'),
      image: FiAccessoriesImage,
      visible: true,
      onClick: () => {
        productListingPage.keepShoppingAccessories();
        history.push(AppPaths.Accessories);
      },
    },
    {
      title: t('supplements'),
      description: t('data_driven_formulas_for_daily_dog_care'),
      image: FiSupplementsImage,
      visible: supplementsCardEnabled && supplementsProductIsSupportedInCountry(country),
      onClick: () => {
        productListingPage.keepShoppingSupplements();
        history.push(AppPaths.Products('supplements'));
      },
    },
    {
      title: t('nano_microchips'),
      description: t('microchips_for_vets_and_rescue_shelters'),
      image: FiNanoMicrochipsImage,
      visible: nanoProductIsSupportedInCountry(country),
      onClick: () => {
        productListingPage.keepShoppingNano();
        window.location.href = `https://${getDomainConfig().nanoDomain}/`;
      },
    },
  ];

  return (
    <div className={classNames('product-listing-page', styles.container)}>
      <QuizAutoOpen />
      {plpV2 ? (
        <header className={v2.hero}>
          <div className={v2.eyebrow}>{t('plp_v2_eyebrow')}</div>
          <h1 className={v2.title}>{t('plp_v2_title')}</h1>
          <p className={v2.subtitle}>{t('plp_v2_subtitle')}</p>
          <StarRating />
          <div className={v2.quizBanner}>
            <IconMagic />
            <div className={v2.quizText}>
              <div className={v2.quizTitle}>{t('plp_v2_quiz_title')}</div>
              <div className={v2.quizBody}>{t('plp_v2_quiz_body')}</div>
            </div>
            <button
              type="button"
              className={v2.quizCta}
              onClick={() => {
                quizQuestionPage.quizButtonTapped();
                launchQuiz();
              }}
            >
              {t('plp_v2_quiz_cta')}
            </button>
          </div>
        </header>
      ) : (
        <Flex direction={'column'} gap={16} align="center">
          <Text fz={isReferralV2 ? 36 : 56} fw={700} lh={1} lts="-1.12px" ta={isReferralV2 ? 'center' : undefined}>
            {isRedeemFlow ? t('exclusive_offer_claim_your_free_fi_collar') : t('trusted_by_millions_of_pet_parents')}
          </Text>
          <Text
            fz={20}
            fw={700}
            lh={1.2}
            lts="-0.4px"
            c="type-light-secondary"
            ta={isReferralV2 ? 'center' : undefined}
          >
            {isRedeemFlow
              ? t('99_off_applied_at_checkout')
              : isReferralV2
                ? referrerName
                  ? t('referral_v2_plp_subline', { referrerName })
                  : t('referral_v2_plp_subline_generic')
                : t('most_advanced_gps_tracking_solutions_for_your_sidekick')}
          </Text>
          {isReferralV2 && <StarRating />}
          {!isRedeemFlow && !isReferralV2 && (
            <Button
              bg={'black'}
              radius={100}
              className={styles.quizPromptButton}
              onClick={() => {
                quizQuestionPage.quizButtonTapped();
                launchQuiz();
              }}
            >
              <Text fz={14} fw={600} lh={1.2} py={10} px={24} lts="-0.14px">
                {t('which_fi_is_right_for_me')}
              </Text>
            </Button>
          )}
        </Flex>
      )}
      <ProductCarousel className={plpV2 ? v2.grid : undefined}>
        {orderedProductCards.map((card) => (
          <React.Fragment key={card.key}>{card.node}</React.Fragment>
        ))}
      </ProductCarousel>

      {plpV2 && (
        <section className={v2.section}>
          <div className={v2.sectionTitle}>{t('plp_v2_included_with_every_fi')}</div>
          <div className={v2.trustRow}>
            {[
              [t('free_shipping'), t('plp_v2_shipping_sub')],
              [t('hardware_first_returns_title'), t('plp_v2_returns_sub')],
              [t('one_year_warranty'), t('cart_item_v2.warranty_description')],
            ].map(([title, sub]) => (
              <div key={title} className={v2.trustTile}>
                <div className={v2.trustTitle}>{title}</div>
                <div className={v2.trustSub}>{sub}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!isReferralV2 && (
        <div className={plpV2 ? v2.compareBand : styles.compareBox}>
          <div>
            <div className={plpV2 ? v2.compareTitle : styles.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
            {plpV2 && <div className={v2.compareBody}>{t('plp_v2_compare_body')}</div>}
          </div>
          <div className={plpV2 ? v2.compareButtons : styles.compareButtonsContainer}>
            <button
              className={plpV2 ? v2.compareHelp : styles.helpMeChooseCta}
              onClick={() => {
                quizQuestionPage.quizButtonTapped();
                launchQuiz();
              }}
            >
              {t('help_me_choose')}
            </button>
            <button
              className={plpV2 ? v2.compareAll : styles.compareCta}
              onClick={() => {
                productListingPage.comparisonCompareProducts();
                history.push(AppPaths.Compare);
              }}
            >
              {plpV2 ? t('plp_v2_compare_all_products') : t('compare_products')}
            </button>
          </div>
        </div>
      )}

      {isFreeTrialVisible && !isRedeemFlow && !inApp && !isReferralV2 && (
        <FreeTrialCard
          onStartFreeTrial={() => {
            productListingPage.freeTrialStartFreeTrial();
            launchQuiz();
          }}
        />
      )}

      {!isReferralV2 && (
        <>
          <div className={plpV2 ? v2.sectionTitle : styles.keepShoppingTitle}>{t('keep_shopping')}</div>
          <ProductCarousel className={plpV2 ? v2.shopRows : undefined}>
            {keepShopping
              .filter((item) => item.visible)
              .map((item) =>
                plpV2 ? (
                  <button key={item.title} type="button" className={v2.shopRow} onClick={item.onClick}>
                    <img className={v2.shopThumb} src={item.image} alt="" />
                    <span>
                      <span className={v2.shopRowTitle}>
                        {item.title}
                        <ArrowRight />
                      </span>
                      <span className={v2.shopRowSub}>{item.description}</span>
                    </span>
                  </button>
                ) : (
                  <ProductCard key={item.title} {...item} cta={t('shop')} />
                ),
              )}
          </ProductCarousel>
        </>
      )}
      <div className={styles.footer} />
    </div>
  );
};

export default ProductListingPage;
